import type { TFunction } from 'i18next';
import { type ReactNode, useState } from 'react';
import { CheckCircleFill, ChevronRight, ExclamationCircleFill } from 'react-bootstrap-icons';

import { MFA_FACTOR, type MfaFactor } from '@repo/common';
import { DataTestIds } from '@repo/frontend-shared';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSIcon } from '@repo/sds/SDSIcon';
import { SDSPrimitiveButton } from '@repo/sds/SDSPrimitiveButton';
import { SDSRadio } from '@repo/sds/SDSRadio';
import { SDSText } from '@repo/sds/SDSText';

import { AuthenticatorAppIcon, EmailCodeMethodIcon, PasskeyMethodIcon, SmsMethodIcon } from './mfa-method-icons';
import { type MfaPickerMethod, resolvePrimaryFactor } from './mfa-method-picker-utils';

const MFA = DataTestIds.COMMON.SECURITY.MFA;

export type MfaPickerLayout = 'desktop' | 'tablet' | 'mobile';

function MethodStatus({ description, enrolled, muted }: { description: string; enrolled: boolean; muted: boolean }) {
  return (
    <span className="MfaMethodChoice__status">
      <SDSIcon
        color={enrolled ? 'primary' : 'warning'}
        icon={enrolled ? <CheckCircleFill /> : <ExclamationCircleFill />}
        size="small"
      />
      <SDSText color={muted ? 'muted' : 'text-secondary'} text={description} type="body-small" />
    </span>
  );
}

function MethodChoiceRow({
  description,
  enrolled,
  icon,
  layout,
  onActivate,
  onSelect,
  selected,
  setupTestId,
  title,
  value,
}: {
  description: string;
  enrolled: boolean;
  icon: ReactNode;
  layout: MfaPickerLayout;
  onActivate: (value: MfaPickerMethod) => void;
  onSelect: (value: MfaPickerMethod) => void;
  selected: boolean;
  setupTestId: string;
  title: string;
  value: MfaPickerMethod;
}) {
  const stacked = layout === 'mobile';
  const badgeIcon = layout !== 'mobile';

  return (
    <div className="MfaMethodChoice__option">
      <SDSRadio
        ariaLabel={title}
        checked={selected}
        dataTestId={`${setupTestId}-radio`}
        onChange={() => onSelect(value)}
        value={value}
      />
      <SDSPrimitiveButton
        ariaLabel={title}
        className="MfaMethodChoice__hit"
        dataTestId={setupTestId}
        onClick={() => onActivate(value)}
      >
        {stacked ? (
          <span className="MfaMethodChoice__stack">
            <span className="MfaMethodChoice__method">
              <span className="MfaMethodChoice__icon">{icon}</span>
              <SDSText text={title} type="body-medium" />
            </span>
            <MethodStatus description={description} enrolled={enrolled} muted />
          </span>
        ) : (
          <>
            <span
              aria-hidden="true"
              className={badgeIcon ? 'MfaMethodChoice__icon MfaMethodChoice__icon--badge' : 'MfaMethodChoice__icon'}
            >
              {icon}
            </span>
            <SDSText className="MfaMethodChoice__title" text={title} type="body-medium" />
            <MethodStatus description={description} enrolled={enrolled} muted={false} />
          </>
        )}
        <span aria-hidden="true" className="MfaMethodChoice__chevron">
          <SDSIcon color="text-secondary" icon={<ChevronRight />} size="small" />
        </span>
      </SDSPrimitiveButton>
    </div>
  );
}

interface MfaMethodPickerProps {
  accountPrimary?: string;
  emailHint: string;
  enabled: boolean;
  enrolled: readonly string[];
  layout: MfaPickerLayout;
  onAccountPrimaryChange?: (method: MfaFactor) => void;
  onDisable: () => void;
  onManageEmail: () => void;
  onManagePasskey: () => void;
  onManageSms: () => void;
  onManageTotp: () => void;
  onSetup: (method: MfaPickerMethod) => void;
  passkeyCount?: number;
  smsPhoneMasked?: string | null;
  /** Offers SMS to an account that has not enrolled it. An enrolled SMS factor is always shown. */
  smsSetupAvailable?: boolean;
  t: TFunction<'common'>;
  totpAppName?: string | null;
}

export function MfaMethodPicker({
  accountPrimary,
  emailHint,
  enabled,
  enrolled,
  layout,
  onAccountPrimaryChange,
  onDisable,
  onManageEmail,
  onManagePasskey,
  onManageSms,
  onManageTotp,
  onSetup,
  passkeyCount = 0,
  smsPhoneMasked,
  smsSetupAvailable = false,
  t,
  totpAppName,
}: MfaMethodPickerProps) {
  const totpEnrolled = enrolled.includes(MFA_FACTOR.Totp);
  const emailEnrolled = enrolled.includes(MFA_FACTOR.Email);
  const smsEnrolled = enrolled.includes(MFA_FACTOR.Sms);
  const passkeyEnrolled = enrolled.includes(MFA_FACTOR.Passkey);
  const resolvedPasskeyCount = passkeyEnrolled ? Math.max(passkeyCount, 1) : 0;
  const [selected, setSelected] = useState<MfaPickerMethod>(() => {
    const primary = resolvePrimaryFactor(enrolled, accountPrimary);
    if (
      primary === MFA_FACTOR.Email ||
      primary === MFA_FACTOR.Totp ||
      primary === MFA_FACTOR.Passkey ||
      primary === MFA_FACTOR.Sms
    ) {
      return primary;
    }
    return MFA_FACTOR.Totp;
  });

  const isLiveEnrolled = (method: MfaPickerMethod): boolean =>
    (method === MFA_FACTOR.Totp && totpEnrolled) ||
    (method === MFA_FACTOR.Email && emailEnrolled) ||
    (method === MFA_FACTOR.Sms && smsEnrolled) ||
    (method === MFA_FACTOR.Passkey && passkeyEnrolled);

  const handleSelect = (method: MfaPickerMethod) => {
    setSelected(method);
    if (isLiveEnrolled(method)) {
      onAccountPrimaryChange?.(method);
    }
  };

  const handleActivate = (method: MfaPickerMethod) => {
    if (method === MFA_FACTOR.Totp && totpEnrolled) {
      onManageTotp();
      return;
    }
    if (method === MFA_FACTOR.Email && emailEnrolled) {
      onManageEmail();
      return;
    }
    if (method === MFA_FACTOR.Sms && smsEnrolled) {
      onManageSms();
      return;
    }
    if (method === MFA_FACTOR.Passkey && passkeyEnrolled) {
      onManagePasskey();
      return;
    }
    if (isLiveEnrolled(method)) {
      handleSelect(method);
      return;
    }
    onSetup(method);
  };

  const totpDescription = totpEnrolled
    ? totpAppName?.trim() || t('security.mfa.totpEnrolledHint')
    : t('security.mfa.totpSetupHint');
  const totpRow = (
    <MethodChoiceRow
      description={totpDescription}
      enrolled={totpEnrolled}
      icon={<AuthenticatorAppIcon />}
      layout={layout}
      onActivate={handleActivate}
      onSelect={handleSelect}
      selected={selected === MFA_FACTOR.Totp}
      setupTestId={MFA.CHOOSE_TOTP}
      title={t('security.mfa.totpTitle')}
      value={MFA_FACTOR.Totp}
    />
  );
  const otherRows = (
    <>
      {smsEnrolled || smsSetupAvailable ? (
        <MethodChoiceRow
          description={
            smsEnrolled && smsPhoneMasked
              ? t('security.mfa.smsEnrolledHint', { masked: smsPhoneMasked })
              : t('security.mfa.smsSetupHint')
          }
          enrolled={smsEnrolled}
          icon={<SmsMethodIcon />}
          layout={layout}
          onActivate={handleActivate}
          onSelect={handleSelect}
          selected={selected === MFA_FACTOR.Sms}
          setupTestId={MFA.CHOOSE_SMS}
          title={t('security.mfa.smsMethodTitle')}
          value={MFA_FACTOR.Sms}
        />
      ) : null}
      <MethodChoiceRow
        description={emailEnrolled ? emailHint : t('security.mfa.emailSetupHint')}
        enrolled={emailEnrolled}
        icon={<EmailCodeMethodIcon />}
        layout={layout}
        onActivate={handleActivate}
        onSelect={handleSelect}
        selected={selected === MFA_FACTOR.Email}
        setupTestId={totpEnrolled ? MFA.EMAIL_ENROLL : MFA.CHOOSE_EMAIL}
        title={t('security.mfa.emailMethodTitle')}
        value={MFA_FACTOR.Email}
      />
      <MethodChoiceRow
        description={
          passkeyEnrolled
            ? t('security.mfa.passkeyEnrolledHint', { count: resolvedPasskeyCount })
            : t('security.mfa.passkeySetupHint')
        }
        enrolled={passkeyEnrolled}
        icon={<PasskeyMethodIcon />}
        layout={layout}
        onActivate={handleActivate}
        onSelect={handleSelect}
        selected={selected === MFA_FACTOR.Passkey}
        setupTestId={MFA.CHOOSE_PASSKEY}
        title={t('security.mfa.passkeyMethodTitle')}
        value={MFA_FACTOR.Passkey}
      />
    </>
  );

  return (
    <SDSFlexbox
      className={`MfaMethodChoice MfaMethodChoice--${layout}`}
      direction="column"
      fullWidth
      gap="text-x-large"
    >
      <SDSFlexbox direction="column" fullWidth gap="text-medium">
        <SDSText color="text-secondary" text={t('security.mfa.selectPrimaryMethod')} type="body-large" />
        {totpRow}
        {otherRows}
      </SDSFlexbox>
      <SDSFlexbox fullWidth justifyContent="center">
        {enabled ? (
          <SDSButton
            dataTestId={MFA.DISABLE_ALL}
            mode="ghost"
            onClick={onDisable}
            status="alert"
            text={t('security.mfa.turnOffTwoStep')}
          />
        ) : (
          <SDSButton
            dataTestId={MFA.TURN_ON}
            mode="ghost"
            onClick={() => onSetup(selected)}
            text={t('security.mfa.turnOnTwoStep')}
          />
        )}
      </SDSFlexbox>
    </SDSFlexbox>
  );
}
